import React from 'react';
import 'react-dates/initialize';
import { SingleDatePicker, SingleDatePickerShape } from 'react-dates';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/en-gb';
import { TranslationProps, WithCoreProps } from 'ff-shared-components-core';
import './datePickerField.less';
import { Icon } from '../../../layout/icon';

export type DatePickerProps = SingleDatePickerShape;

export interface DatePickerInnerProps {
	name?: string;
	dateFieldLabel?: string;
	placeholder?: string;
	isDisabled?: boolean;
	hasError?: boolean;
	errorMessage?: string;
	minDate?: moment.Moment;
	maxDate?: moment.Moment;
	yearsBefore?: number;
	yearsAfter?: number;
}

type Props = DatePickerProps & DatePickerInnerProps & TranslationProps;

export interface DatePickerState {
	calendarFocused: boolean | null;
	minDate: moment.Moment;
	maxDate: moment.Moment;
}

class DatePickerFieldComponent extends React.PureComponent<Props, DatePickerState> {
	constructor(props: Props) {
		super(props);
		// Set Translation to calendar
		const lang = 'pt'; // StorageHelper.getFromLocal('language');
		lang === 'pt' ? moment.locale(lang) : moment.locale('en-GB');

		const { maxDate, minDate, yearsAfter, yearsBefore } = this.props;
		// Set years dropdown; default: 5 years before and 2 years after
		this.state = {
			calendarFocused: false,
			minDate: minDate ? minDate.subtract(yearsBefore, 'year') : moment().subtract(5, 'year'),
			maxDate: maxDate ? maxDate.add(yearsAfter, 'year') : moment().add(2, 'year'),
		};
	}

	componentDidUpdate(): void {
		// Set Translation to calendar
		const lang = 'pt'; // StorageHelper.getFromLocal('language');
		lang === 'pt' ? moment.locale(lang) : moment.locale('en-GB');
	}

	onFocusChange = ({ focused }: { focused: boolean | null }): void =>
		this.setState(() => ({ calendarFocused: focused }));
	isOutsideRange = (day: moment.Moment): boolean =>
		day.isAfter(this.props.maxDate) || day.isBefore(this.props.minDate);

	renderYears = (): JSX.Element[] => {
		const { maxDate, minDate } = this.state;
		//get year difference
		const years: number = maxDate && maxDate.diff(minDate, 'years', false);
		const options: JSX.Element[] = [];

		for (let index = 0; index <= years; index++) {
			if (maxDate) {
				options.push(
					<option key={index} value={maxDate.year() - index}>
						{maxDate.year() - index}
					</option>
				);
			}
		}
		return options;
	};

	renderMonthElement = ({
		month,
		onMonthSelect,
		onYearSelect,
	}: {
		month: moment.Moment;
		onMonthSelect: (currentMonth: moment.Moment, newMonthVal: string) => void;
		onYearSelect: (currentMonth: moment.Moment, newYearVal: string) => void;
	}): JSX.Element => (
		<div className="inputMonthYear">
			{/* months */}
			<div className="dropdownM">
				<select value={month.month()} onChange={(e): void => onMonthSelect(month, e.target.value)}>
					{moment.months().map((label, value) => (
						<option className="option" key={value} value={value}>
							{label}
						</option>
					))}
				</select>
			</div>
			{/* years */}
			<div className="dropdownY">
				<select value={month.year()} onChange={(e): void => onYearSelect(month, e.target.value)}>
					{this.renderYears()}
				</select>
				{/* left here for future use*/}
				{/* <input
					type="text"
					name="year"
					defaultValue={month.year()}
					minLength={0}
					maxLength={4}
					pattern="[0-9]{4}"
					onChange={e => {
						if (e.currentTarget.value.toString().length === 4) {
							onYearSelect(month, e.currentTarget.value);
						}
					}}
				/> */}
			</div>
		</div>
	);

	render(): React.ReactNode {
		const {
			name,
			dateFieldLabel,
			placeholder,
			hasError = false,
			errorMessage = '',
			isDisabled = false,
			t,
			children,
			...res
		} = this.props;

		return (
			<div className="ds-input-field-content flex-inline">
				<label htmlFor={name}>{dateFieldLabel}</label>
				<div className="ds-input-textbox">
					<SingleDatePicker
						{...res}
						// extras
						disabled={isDisabled}
						enableOutsideDays={false}
						isOutsideRange={day => false}
						numberOfMonths={1}
						placeholder={placeholder}
						noBorder={true}
						showDefaultInputIcon={true}
						inputIconPosition={'after'}
						focused={this.state.calendarFocused}
						onFocusChange={this.onFocusChange}
						/* X icon*/
						hideKeyboardShortcutsPanel={true}
						customInputIcon={<Icon name="calendar-icon" />}
						//navigation
						transitionDuration={100}
						renderMonthElement={this.renderMonthElement}
						initialVisibleMonth={(): moment.Moment => (this.props.date ? this.props.date : moment())}
						//format
						displayFormat={res.date ? res.date.locale('pt').format('L') : ''}
						dayAriaLabelFormat={res.date ? res.date.locale('pt').format('L') : ''}
						readOnly
					/>
				</div>
				<div className="input-error-text">
					<span>{`${hasError ? errorMessage : ''}`}</span>
				</div>
			</div>
		);
	}
}

export const DatePickerField = WithCoreProps<any>(DatePickerFieldComponent);
